/** Workflows P6 (M26, DEC-099): the Default workflows section, the pause / restore / duplicate dialogs and the builder of a default. Merged into `engage.workflows`. */
export const workflowsDefaults = {
  title: "Default workflows",
  intro: "These run for everyone in your workspace on every plan and don't count toward your limits. Edit them to change who is alerted; pause one to stop it.",
  own: {
    title: "Your workflows",
    subtitle: "Workflows you made. They run after the default ones.",
  },
  areas: {
    conversations: "Conversations and handoff",
    leads: "Leads and bookings",
    knowledge: "Knowledge",
    developers: "Developers",
    service_desk: "Service desk",
  },
  badge: "Default",
  modified: "Modified",
  updateAvailable: "Update available",
  offByDefault: "Off by default",
  runs_one: "{{count}} run in the last 30 days",
  runs_other: "{{count}} runs in the last 30 days",
  switchOn: "Switch on",
  switchOnLabel: "Switch on {{name}}",
  switchedOn: "{{name}} is on",
  paused: "{{name}} is paused",
  resumed: "{{name}} is live again",
  kpiDefaults: "+{{count}} default",
  kpiRunsHint: "Runs and failed runs include the default workflows.",
  kpiLabel: "Workflow summary, last 30 days",
  banner: {
    title_one: "{{count}} default workflow is paused",
    title_other: "{{count}} default workflows are paused",
    resume: "Resume",
    resumeLabel: "Resume {{name}}",
  },
  menu: {
    label: "Actions for {{name}}",
    open: "Open",
    pause: "Pause",
    resume: "Resume",
    duplicate: "Duplicate",
    restore: "Restore default",
    restoreDisabled: "Restore default (nothing was changed)",
    compare: "Compare with default",
  },
  pause: {
    title: "Pause “{{name}}”?",
    stops: "Pausing this stops:",
    window: "Numbers are from the last {{days}} days.",
    nothing: "In the last {{days}} days it did not send or assign anything, so pausing changes nothing you would have noticed.",
    people_one: "{{count}} person",
    people_other: "{{count}} people",
    alerts_one: "{{count}} alert to {{people}}",
    alerts_other: "{{count}} alerts to {{people}}",
    mailboxes_one: "{{count}} e-mail to a shared mailbox",
    mailboxes_other: "{{count}} e-mails to shared mailboxes",
    deliveries_one: "{{count}} delivery to your webhook endpoints",
    deliveries_other: "{{count}} deliveries to your webhook endpoints",
    assignments_one: "{{count}} conversation or lead given to someone",
    assignments_other: "{{count}} conversations or leads given to someone",
    loading: "Counting what it did in the last 30 days",
    impactFailed: "We couldn't load the numbers. You can still pause it.",
    memberSettings: "Members' notification settings can't turn these back on.",
    endpointsKeep: "Your endpoints keep their subscriptions. They receive nothing until you resume this workflow.",
    confirmAlerts: "Pause and stop these alerts",
    confirmDeliveries: "Pause and stop these deliveries",
  },
  restore: {
    title: "Restore “{{name}}”?",
    body: "This puts the original default back in place of your changes. Your current version stays in the version history.",
    bodyUpdate: "Choose which default to put back.",
    target: "Restore to",
    targetBaseline: "The default you started with",
    targetBaselineHint: "Puts back the steps and settings this workflow was given.",
    targetLatestPlain: "The newest default",
    targetLatest: "The newest default (revision {{revision}})",
    targetLatestHint: "The workflow moves to the newest wording and steps, and the newest default becomes the one Restore default returns to. It is always published.",
    publish: "Publish it now",
    publishHint: "Turn this off to replace only the draft, so you can look at it first. Whether the workflow runs does not change.",
    publishLatest: "The newest default is always published.",
    unpublished: "This workflow has changes that are not published yet. Restoring replaces them.",
    unsaved: "You have edits that are not saved. Restoring replaces them.",
    changes: "What changes",
    nothing: "This workflow already matches the default.",
    yours: "Your version",
    original: "Default",
    confirmPublish: "Restore and publish",
    confirmDraft: "Restore to the draft",
    confirmLatest: "Update and publish",
    restored: "Restored. Version {{version}} is live.",
    restoredDraft: "Restored to the draft. Publish it when you are ready.",
    updated: "Updated to the newest default. Version {{version}} is live.",
    wasPaused: "It stays paused until you resume it.",
  },
  compare: {
    title: "Compare with default",
    hint: "The steps of this workflow next to the default it started from.",
    baseline: "Default",
    modifiedNote: "Your version differs from the default.",
  },
  duplicate: {
    title: "Duplicate “{{name}}”",
    body: "You get a workflow of your own to change freely. The default keeps running. Pause it if your copy replaces it, or both will send.",
    swapped: "Steps that only a default workflow can use are swapped or left out, and the draft says which.",
    name: "Name of the copy",
    copyName: "Copy of {{name}}",
    confirm: "Create the copy",
    created: "Copy created as a draft",
  },
  upgrade: {
    title: "Build your own workflows on Standard",
    body: "The default workflows above already cover your alerts, assignment and webhook events on every plan. With Standard you can also make workflows of your own: welcome menus, lead rules, follow-ups and more.",
    cta: "See plans",
    sheetTitle: "Workflows of your own are on Standard",
    sheetBody: "Creating a workflow, starting from a template or duplicating a default needs the Standard plan or above. The default workflows keep running and you can still edit and pause them.",
    sheetClose: "Not now",
    lock: "Needs the {{plan}} plan",
    locked: "Locked on your plan",
  },
  builder: {
    bannerTitle: "Default workflow",
    banner: "It runs on every plan and doesn't count toward your run limit. You can change its steps, pause it or restore the original at any time.",
    bannerStarter: "On your plan you can edit and pause it. Duplicating it needs the Standard plan.",
    bannerModified: "You changed this workflow. Restore default puts the original back.",
    bannerUpdate: "A newer default is available. Restore default lets you update to it.",
    restore: "Restore default",
    compare: "Compare with default",
    duplicate: "Duplicate",
    triggerLocked: "A default workflow's trigger can't change. Duplicate it to start from another trigger.",
    triggerLockedBadge: "Locked",
    deleteReason: "Default workflows can't be deleted. Pause it instead.",
    pausedNote: "This workflow is paused. {{impact}}",
    lockedStep: "Not available in default workflows: duplicate to use it",
    lockedStepPicked: "Default workflows can't use {{step}}. Duplicate this workflow to use it.",
    lockedStepPickedStarter: "Default workflows can't use {{step}}. Duplicating a workflow needs the Standard plan.",
    onlyUnder: "Only in the “{{trigger}}” workflow",
    limits: "A default workflow has at most {{paths}} paths and {{steps}} steps in a path.",
    addStepHint: "Default workflows use alerts, routing, tags, notes, priority and conditions. Other steps are in the list but locked.",
    notInDefaults: "not in default workflows",
    restoredToast: "Restored",
  },
  events: {
    title: "Events it sends",
    all: "All events your endpoints subscribe to",
    only: "Only these events",
    mode: "Which events",
    subscribed: "Your endpoints currently subscribe to:",
    none: "No endpoint subscribes to an event yet. Add one in Settings → Webhooks.",
    noAccess: "You need permission to manage webhooks to see which events your endpoints subscribe to.",
    loadFailed: "We couldn't load your endpoints.",
    required: "Choose at least one event, or send all events.",
    hint: "Each endpoint still chooses its own events in Settings → Webhooks. This setting can only narrow what this workflow sends.",
    link: "Open webhook settings",
  },
  cross: {
    names: {
      handoff_route_and_alert: "Route and alert new handoffs",
      new_lead_route_and_alert: "Assign and announce new leads",
      developer_webhooks: "Send events to your webhook endpoints",
    },
    open: "Open the workflow",
    paused: "“{{name}}” is paused. {{impact}}",
    handoffSettings: "These settings are used by the “{{name}}” default workflow.",
    leadsRouting: "This rule is used by the “{{name}}” default workflow.",
    notifications: "Which events notify your team is set in Workflows → Default workflows. Here you choose how you receive them.",
    notificationsLink: "Open default workflows",
    routedBy: "Routed by",
    routedByDefault: "A default workflow",
  },
  standard: {
    title: "Standard message",
    body: "People get the same wording they always have. Here is an example.",
    preview: "Example",
    writeOwn: "Write my own",
    writeOwnHint: "Switches to a message you write. It starts with the example text.",
    custom: "Your own message",
    useStandard: "Use the standard message",
    noStandard: "This workflow has no standard message. Write your own.",
    samples: {
      handoff_requested: { title: "Maria Cruz asked for a person", body: "Admissions Assistant · Wants to talk about tuition" },
      handoff_reminder_due: { title: "Maria Cruz has waited 5 minutes for a reply", body: "Admissions Assistant · Wants to talk about tuition" },
      handoff_escalation_due: { title: "Maria Cruz is still waiting after 15 minutes", body: "Admissions Assistant · Wants to talk about tuition" },
      handoff_waiting_digest_due: { title: "3 visitors are waiting for you", body: "Visitors asked for a person while the office was closed." },
      conversation_assigned: { title: "Maria Cruz was assigned to you", body: "Admissions Assistant · Wants to talk about tuition" },
      conversation_returned_to_queue: { title: "Maria Cruz is back in the Admissions queue", body: "Admissions Assistant · Wants to talk about tuition" },
      lead_created: { title: "New lead from Admissions Assistant", body: "Maria Cruz asked about Grade 7 enrolment." },
      lead_followed_up: { title: "New request from Maria Cruz", body: "Asks if there is still a slot on Saturday." },
      lead_updated: { title: "A lead was assigned to you", body: "Maria Cruz" },
      booking_requested: { title: "Appointment requested: Maria Cruz", body: "Campus tour - Sat, Nov 7, 10:00 AM" },
      booking_confirmed: { title: "Appointment booked: Maria Cruz", body: "Campus tour - Sat, Nov 7, 10:00 AM" },
      source_processed: { title: "“Admissions handbook” is ready", body: "The assistant can now answer from this source." },
      source_failed: { title: "“Admissions handbook” could not be processed", body: "Processing failed. Upload a new copy or contact support." },
      gap_assigned: { title: "A knowledge gap was assigned to you", body: "What are the scholarship deadlines?" },
    },
  },
} as const;
